import type { ChartPoint, DateRange, ZoomPercent } from "~/types/ui";

/**
 * Helpers for "which slice of time is on screen, and how do compare series map onto
 * it" — shared by every caller of `TimeSeriesChart` (the dashboard chart widget today,
 * SensorScope's nodes-chart when it picks up compare ranges) so they all crop and align
 * data the same way.
 */

/**
 * What "+ Compare" seeds a new compare range with.
 */
export const DEFAULT_COMPARE_START = "-7d";

/** Narrows a date range to the sub-window a zoom percentage (0-100) selects.
 *
 * Anchored on a range the caller owns rather than on echarts' reported axis extent:
 * echarts' "time" axis applies "nice" boundary snapping to its *effective* extent even
 * when explicit min/max are set, so reading the extent back yields a window that doesn't
 * match what's actually drawn.
 */
export function applyZoomPercent(
  range: DateRange,
  percent: ZoomPercent,
): DateRange {
  const spanMs = range.to.getTime() - range.from.getTime();
  return {
    from: new Date(range.from.getTime() + (percent.start / 100) * spanMs),
    to: new Date(range.from.getTime() + (percent.end / 100) * spanMs),
  };
}

/** Drops points outside `window`. Cropping the data itself — rather than leaving it in
 * and letting echarts' dataZoom merely hide the overflow — is what lets a caller
 * guarantee that anything it calculates (sums, averages, a legend chip) covers exactly
 * what the viewer can see. */
export function cropToWindow(
  points: ChartPoint[],
  window: DateRange,
): ChartPoint[] {
  const from = window.from.getTime();
  const to = window.to.getTime();
  return points.filter(([t]) => t >= from && t <= to);
}

/** How far a compare range sits from the main one. Since a compare window is always
 * exactly as long as the main range (see `resolveCompareRange`), this single constant
 * fully describes it — which is why a compare series can simply be slid onto the main
 * axis instead of needing an overlapping axis of its own. */
export function compareTimeOffset(
  compareRange: DateRange,
  range: DateRange,
): number {
  return compareRange.from.getTime() - range.from.getTime();
}

/** Slides a compare range's points onto the main window (`plotted = real - offset`) so
 * they line up with the main series on one shared axis. Pass the same offset as
 * `SeriesConfig.timeOffsetMs` so tooltips and exports can recover the real timestamps. */
export function shiftPointsToMainWindow(
  points: ChartPoint[],
  offsetMs: number,
): ChartPoint[] {
  return points.map(([t, value]) => [t - offsetMs, value] as ChartPoint);
}
